import React, { useState, useCallback, useEffect } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_URL } from '../config';
import EditorialShell from './EditorialShell';
import BuildCommercialStep1 from './BuildCommercialStep1';
import BuildCommercialStep2 from './BuildCommercialStep2';
import BuildCommercialStep3 from './BuildCommercialStep3';
import BuildCommercialStep4 from './BuildCommercialStep4';
import BuildCommercialStep5 from './BuildCommercialStep5';
import BuildCommercialStep6 from './BuildCommercialStep6';
import BuildCommercialStep7 from './BuildCommercialStep7';
import BuildCommercialStep8 from './BuildCommercialStep8';

const BuildCommercialApp: React.FC = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [superpossibleKey, setSuperpossibleKey] = useState('');
  
  // Form data for build commercial demo
  const [userName, setUserName] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [styleAndVibe, setStyleAndVibe] = useState('');
  const setStyleAndVibeWithPrompt = (audience: string, internal_prompt: string) => {
    setStyleAndVibe(audience);
    setInternalPrompt(internal_prompt);
  };
  const [internalPrompt, setInternalPrompt] = useState('');
  const [character, setCharacter] = useState<string>('');
  const [customCharacter, setCustomCharacter] = useState('');
  const [characterDescription, setCharacterDescription] = useState<string>('');
  const [commercialScript, setCommercialScript] = useState('');
  const [finalCommercial, setFinalCommercial] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  // The storyboard script the current video was shot from.
  const [videoScript, setVideoScript] = useState('');
  const [customStyleLocation, setCustomStyleLocation] = useState('');
  const [tone, setTone] = useState('');
  const [publishData, setPublishData] = useState<any>(null);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  // Search state - moved up to persist across steps
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  // Load superpossible_key from localStorage on component mount
  useEffect(() => {
    const savedSuperpossibleKey = localStorage.getItem('superpossible_key');
    if (savedSuperpossibleKey) {
      setSuperpossibleKey(savedSuperpossibleKey);
    }
  }, []);


  const processStep = async (data: any): Promise<any> => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/build-commercial/process-step`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, superpossible_key: superpossibleKey, ...data })
      });
      
      const result = await response.json();
      
      if (!sessionId) setSessionId(result.session_id);
      
      return result;
    } catch (error) {
      console.error('API Error:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleStep1 = async () => {
    if (!userName.trim()) return;
    await processStep({ user_name: userName });
    setStep(2);
  };

  const handleStep2 = async () => {
    if (!selectedProduct) return;
    await processStep({ selected_product: selectedProduct });
    setStep(3);
  };

  const handleStep3 = async () => {
    if (!styleAndVibe) return;
    await processStep({ 
      style_and_vibe: styleAndVibe,
      internal_prompt: internalPrompt,
      custom_style_location: customStyleLocation 
    });
    setStep(4);
  };

  const handleCharacterChange = (characterId: string, description: string) => {
    setCharacterDescription(description);
  };

  const handleStep4 = async () => {
    if (!character) return;

    const descriptionToSend = (
      character === 'wild-card' ? customCharacter : characterDescription || customCharacter
    ).trim();
    if (character === 'wild-card' && !descriptionToSend) return;
    
    // Find the character type (label) from the characterOptions
    const characterOptions = [
      { id: 'solo-star', label: 'Solo Star' },
      { id: 'dynamic-duo', label: 'Dynamic Duo' },
      { id: 'the-expert', label: 'The Expert' },
      { id: 'wild-card', label: 'Wild Card' }
    ];
    const selectedCharacterOption = characterOptions.find(option => option.id === character);
    const characterType = selectedCharacterOption ? selectedCharacterOption.label : '';
    
    await processStep({ 
      character: descriptionToSend || characterType,
      character_type: characterType,
      custom_character: customCharacter 
    });
    setStep(5);
  };

  const handleStep5 = async () => {
    if (!tone) return;
    await processStep({ tone: tone });
    setStep(6);
  };

  const handleStep6 = async () => {
    if (!commercialScript) return;

    // A cut only survives if it was shot from the script still on screen.
    // Editing or reselecting the storyboard invalidates the footage.
    if (commercialScript !== videoScript) {
      setVideoUrl('');
      setVideoScript('');
      setPublishData(null);
    }

    // Generate the final commercial
    const result = await processStep({ 
      commercial_script: commercialScript, 
      final_approval: true 
    });
    
    if (result?.final_commercial) {
      setFinalCommercial(result.final_commercial);
    } else {
      // Fallback: use the commercial script as final commercial
      setFinalCommercial(commercialScript);
    }
    
    setStep(7);
  };

  const handleStep7 = async () => {
    // Trigger publishing and navigate to Step 8
    setIsPublishing(true);
    setStep(8); // Navigate to Step 8 immediately to show the publishing state
    
    try {
      console.log('🚀 Publishing commercial...', {
        sessionId,
        finalCommercial,
        videoUrl,
        selectedProduct,
        userName
      });

      const response = await fetch(`${API_URL}/api/build-commercial/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          video_url: videoUrl,
          product: selectedProduct,
          user_name: userName,
          commercial_script: finalCommercial,
          superpossible_key: superpossibleKey
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to publish commercial');
      }

      const data = await response.json();
      
      if (data.success) {
        // Store publish results for Step 8
        setPublishData(data);
        console.log('✅ Commercial published successfully!');
      } else {
        throw new Error(data.error || 'Publishing failed');
      }
      
    } catch (error) {
      console.error('Publishing error:', error);
      toast.error('Failed to publish commercial. Please try again.');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleStep8 = async () => {
    // Step 8 is just for display, no additional logic needed
    console.log('Step 8 completed');
  };

  const handleVideoReady = (url: string) => {
    setVideoUrl(url);
    setVideoScript(commercialScript);
  };

  // Everything the storyboard is written from. If the user goes back and
  // changes any of it, the scripts on Step 6 are rewritten rather than
  // restored from the previous run.
  const creativeSignature = [
    selectedProduct?.id ?? '',
    styleAndVibe,
    internalPrompt,
    customStyleLocation.trim(),
    character,
    characterDescription.trim(),
    customCharacter.trim(),
    tone,
  ].join('::');

  // Search function - moved up to persist results across steps
  const searchProducts = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    setSearchError('');
    
    console.log(`🔍 Starting search for: "${query.trim()}"`);

    try {
      const response = await fetch(`${API_URL}/api/search-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: query.trim(), limit: 15, superpossible_key: superpossibleKey })
      });

      const result = await response.json();
      console.log('🔍 Search response:', result);

      if (result.success) {
        console.log(`✅ Found ${result.count} products. Total searched: ${result.total_searched || 'unknown'}`);
        setSearchResults(result.products);
        if (result.products.length > 0) {
          console.log('📦 First product:', result.products[0]);
        }
      } else {
        console.error('❌ Search failed:', result);
        setSearchError(result.message || `Failed to search products: ${result.error || 'Unknown error'}`);
        setSearchResults([]);
      }
    } catch (error) {
      console.error('❌ Search network error:', error);
      setSearchError('Unable to connect to search service. Please check your connection.');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [superpossibleKey]);

  const stepLabels = [
    { label: 'Title Card' },
    { label: 'Casting the Product' },
    { label: 'Direction' },
    { label: 'Casting' },
    { label: "Writers' Room" },
    { label: 'Storyboard' },
    { label: 'On Set' },
    { label: 'Distribution' },
  ];

  // Check if current step can proceed
  const canProceed = () => {
    switch (step) {
      case 1: return userName.trim() !== '';
      case 2: return selectedProduct !== null;
      case 3: return styleAndVibe !== '' && (styleAndVibe !== 'create-your-own' || customStyleLocation.trim() !== '');
      case 4: return character !== '' && (character !== 'wild-card' || customCharacter.trim() !== '');
      case 5: return tone !== '';
      case 6: return commercialScript !== '';
      case 7: return videoUrl !== '' && finalCommercial !== '';
      case 8: return true;
      default: return false;
    }
  };

  // Get current step handler
  const getCurrentStepHandler = () => {
    switch (step) {
      case 1: return handleStep1;
      case 2: return handleStep2;
      case 3: return handleStep3;
      case 4: return handleStep4;
      case 5: return handleStep5;
      case 6: return handleStep6;
      case 7: return handleStep7;
      case 8: return handleStep8;
      default: return () => {};
    }
  };

  const actionNote = () => {
    switch (step) {
      case 1:
        return userName.trim() ? 'Ready when you are.' : 'Enter your name to begin.';
      case 2:
        return selectedProduct ? `Starring ${selectedProduct.title}.` : 'Choose a product.';
      case 3:
        return styleAndVibe ? 'Ready when you are.' : 'Choose a style.';
      case 4:
        return character ? 'Ready when you are.' : 'Choose your cast.';
      case 5:
        return tone ? 'Ready when you are.' : 'Choose a tone.';
      case 6:
        return commercialScript ? 'Generating takes about a minute.' : 'Select a storyboard.';
      case 7:
        return videoUrl ? 'Publish to attach it to the product page.' : 'Waiting for the final cut.';
      default:
        return '';
    }
  };

  const nextLabel = step === 6 ? 'Generate' : step === 7 ? 'Publish' : 'Continue';

  return (
    <EditorialShell
      studio="commercial"
      chapter="Commercial Studio"
      steps={stepLabels}
      currentStep={step}
      onStepSelect={setStep}
      actions={
        step < 8 ? (
          <>
            <button
              type="button"
              className="ed-btn ed-btn--bare"
              onClick={() => setStep(step - 1)}
              disabled={step === 1}
            >
              <ArrowLeft size={15} />
              Back
            </button>

            <span className="ed-actionbar-note">{actionNote()}</span>

            <button
              type="button"
              className="ed-btn ed-btn--primary"
              onClick={getCurrentStepHandler()}
              disabled={!canProceed() || loading || isPublishing}
            >
              {loading || isPublishing ? (
                <>
                  <span className="ed-spinner" aria-hidden="true" />
                  {isPublishing ? 'Publishing…' : 'Working…'}
                </>
              ) : (
                <>
                  {nextLabel}
                  <ArrowRight size={15} />
                </>
              )}
            </button>
          </>
        ) : undefined
      }
    >
      {step === 1 && (
        <BuildCommercialStep1
          userName={userName}
          setUserName={setUserName}
          onEnterPress={getCurrentStepHandler()}
          loading={loading}
        />
      )}

      {step === 2 && (
        <BuildCommercialStep2
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          loading={loading}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchResults={searchResults}
          searching={searching}
          searchError={searchError}
          onSearch={searchProducts}
        />
      )}

      {step === 3 && (
        <BuildCommercialStep3
          targetAudience={styleAndVibe}
          setTargetAudience={setStyleAndVibeWithPrompt}
          internalPrompt={internalPrompt}
          setInternalPrompt={setInternalPrompt}
          customStyleLocation={customStyleLocation}
          setCustomStyleLocation={setCustomStyleLocation}
          loading={loading}
        />
      )}

      {step === 4 && (
        <BuildCommercialStep4
          character={character}
          setCharacter={setCharacter}
          customCharacter={customCharacter}
          setCustomCharacter={setCustomCharacter}
          characterDescription={characterDescription}
          loading={loading}
          onCharacterChange={handleCharacterChange}
        />
      )}

      {step === 5 && <BuildCommercialStep5 tone={tone} setTone={setTone} loading={loading} />}

      {step === 6 && (
        <BuildCommercialStep6
          commercialScript={commercialScript}
          setCommercialScript={setCommercialScript}
          loading={loading}
          sessionId={sessionId}
          creativeSignature={creativeSignature}
          castBrief={(character === 'wild-card' ? customCharacter : characterDescription).trim()}
        />
      )}

      {step === 7 && (
        <BuildCommercialStep7
          finalCommercial={finalCommercial}
          userName={userName}
          selectedProduct={selectedProduct}
          sessionId={sessionId}
          existingVideoUrl={commercialScript === videoScript ? videoUrl : ''}
          castBrief={(character === 'wild-card' ? customCharacter : characterDescription).trim()}
          onPublish={handleStep7}
          onVideoReady={handleVideoReady}
        />
      )}

      {step === 8 && (
        <BuildCommercialStep8
          finalCommercial={finalCommercial}
          userName={userName}
          selectedProduct={selectedProduct}
          videoUrl={videoUrl}
          sessionId={sessionId}
          publishData={publishData}
          isPublishing={isPublishing}
        />
      )}
    </EditorialShell>
  );
};

export default BuildCommercialApp;
